import React, { useState } from 'react';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { NotificationPromptModal, IOSInstallGuide } from '../NotificationPromptModal';
import { useAuthStore } from '../../store/useAuthStore';

/**
 * Componente global responsável por gerenciar o fluxo de Web Push Notifications.
 *
 * Comportamento:
 * - Após login, espera 1.5-2s e exibe modal solicitando permissão de notificações
 * - No iPhone fora de modo PWA: exibe guia "Adicionar à Tela de Início"
 * - No iPhone em modo PWA: exibe modal de permissão nativo iOS-friendly
 * - Em outros dispositivos: pede permissão diretamente (sem modal se já tem permissão)
 * - Registra a push subscription no backend para notificações mesmo com app fechado
 *
 * Coloque este componente dentro de <Hooks> no App.tsx para que funcione globalmente.
 */
export const PushNotificationsWrapper: React.FC = () => {
  const user = useAuthStore((s) => s.user);

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [pendingRequestFn, setPendingRequestFn] = useState<(() => void) | null>(null);

  const { isIOS } = usePushNotifications({
    enabled: !!user?.id,
    userId: user?.id ?? null,

    onPromptNeeded: (requestFn) => {
      setPendingRequestFn(() => requestFn);
      setShowPermissionModal(true);
    },

    onIOSInstallNeeded: () => {
      setShowIOSGuide(true);
    },
  });

  const handleAcceptPermission = () => {
    setShowPermissionModal(false);
    pendingRequestFn?.();
  };

  const handleDeclinePermission = () => {
    setShowPermissionModal(false);
  };

  return (
    <>
      <NotificationPromptModal
        isOpen={showPermissionModal}
        onAccept={handleAcceptPermission}
        onDecline={handleDeclinePermission}
        isIOS={isIOS}
      />

      <IOSInstallGuide
        isOpen={showIOSGuide}
        onClose={() => setShowIOSGuide(false)}
      />
    </>
  );
};
